import axios from 'axios'
import React, { useState } from 'react'
import moment from 'moment'
import { Modal, Box, CircularProgress } from '@material-ui/core'
import { Button, Input, FormGroup, Label } from 'reactstrap'
import Swal from 'sweetalert2'
import { saveAs } from 'file-saver'
import DateTimeBoostrap from '../../../components/Forms/DateTimeBootstrap'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90vw', md: '60vw', lg: '40vw' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px'
}

/**
 * Position ("switch") changes are reported separately from department
 * transfers, so management can read the two metrics apart instead of one
 * inflated transfer figure.
 */
const ExportSwitchesModal = ({ departments }) => {
  const [open, setOpen] = useState(false)
  const [fromDateValue, setFromDateValue] = useState('')
  const [toDateValue, setToDateValue] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [isExporting, setIsExporting] = useState(false)
  const [dateError, setDateError] = useState('')

  const today = moment().format('DD-MM-YYYY, HH:mm')

  const handleOpen = () => {
    setOpen(true)
    resetForm()
  }

  const handleClose = () => {
    if (!isExporting) {
      setOpen(false)
      resetForm()
    }
  }

  const resetForm = () => {
    setFromDateValue('')
    setToDateValue('')
    setSelectedDepartment('')
    setSelectedType('all')
    setDateError('')
  }

  const onFromDateChange = (date) => {
    if (typeof date === 'object' && date._isAMomentObject) {
      setFromDateValue(moment(date).format('YYYY-MM-DD'))
    } else if (date === '') {
      setFromDateValue('')
    }
    setDateError('')
  }

  const onToDateChange = (date) => {
    if (typeof date === 'object' && date._isAMomentObject) {
      setToDateValue(moment(date).format('YYYY-MM-DD'))
    } else if (date === '') {
      setToDateValue('')
    }
    setDateError('')
  }

  const validateDates = () => {
    if ((fromDateValue && !toDateValue) || (!fromDateValue && toDateValue)) {
      setDateError('Duhet të plotësohen të dy datat ose asnjëra.')
      return false
    }

    if (fromDateValue && toDateValue) {
      if (moment(fromDateValue).isAfter(moment(toDateValue))) {
        setDateError('Data e fillimit duhet të jetë para ose e barabartë me datën e mbarimit.')
        return false
      }
    }

    setDateError('')
    return true
  }

  const handleExport = async () => {
    if (!validateDates()) {
      return
    }

    setIsExporting(true)

    const params = {}
    if (fromDateValue) {
      params.fromDate = fromDateValue
    }
    if (toDateValue) {
      params.toDate = toDateValue
    }
    if (selectedDepartment && selectedDepartment !== 'all') {
      params.department_id = selectedDepartment
    }
    if (selectedType && selectedType !== 'all') {
      params.change_type = selectedType
    }

    try {
      const response = await axios.get('/users/switches', {
        params,
        responseType: 'blob'
      })

      if (response.headers['content-type'] === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        saveAs(response.data, `Switches (${today}).csv`)
        Swal.fire({
          icon: 'success',
          title: 'Sukses!',
          text: 'Eksporti u krye me sukses.',
          timer: 2000,
          showConfirmButton: false
        })
        handleClose()
      }
    } catch (error) {
      console.error('Export error:', error)
      Swal.fire({
        icon: 'error',
        title: 'Gabim!',
        text: 'Ndodhi një gabim gjatë eksportimit.'
      })
    } finally {
      setIsExporting(false)
    }
  }

  const clearFromDate = () => {
    setFromDateValue('')
    setDateError('')
  }

  const clearToDate = () => {
    setToDateValue('')
    setDateError('')
  }

  return (
    <>
      <Button color='info' className='btn-oval mx-2 mb-2' onClick={handleOpen}>
        Shkarko ndryshimet e pozitës
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='export-switches-modal'
        aria-describedby='export-switches-modal-description'
      >
        <Box sx={style}>
          <h2 style={{ opacity: 0.7, marginBottom: '25px', fontSize: '1.5rem' }}>
            Eksporto Ndryshimet e Pozitës
          </h2>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
              <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                <Label className='col-form-label'>Prej datës:</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ flex: 1 }}>
                    <DateTimeBoostrap
                      key={`switch-from-${fromDateValue}`}
                      onChange={onFromDateChange}
                      value={fromDateValue ? moment(fromDateValue).format('DD/MM/YYYY') : ''}
                      utc={true}
                      locale='de'
                      dateFormat='DD/MM/YYYY'
                      timeFormat=''
                      closeOnSelect
                      initialViewMode='years'
                      inputProps={{
                        id: 'switchFromDate',
                        className: 'form-control',
                        name: 'switchFromDate',
                        placeholder: 'Zgjedh datën'
                      }}
                    />
                  </div>
                  {fromDateValue && (
                    <Button color='link' size='sm' onClick={clearFromDate} style={{ padding: '0 5px' }}>
                      ✕
                    </Button>
                  )}
                </div>
              </FormGroup>

              <FormGroup style={{ flex: 1, marginBottom: 0 }}>
                <Label className='col-form-label'>Deri më:</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ flex: 1 }}>
                    <DateTimeBoostrap
                      key={`switch-to-${toDateValue}`}
                      onChange={onToDateChange}
                      value={toDateValue ? moment(toDateValue).format('DD/MM/YYYY') : ''}
                      utc={true}
                      locale='de'
                      dateFormat='DD/MM/YYYY'
                      timeFormat=''
                      closeOnSelect
                      initialViewMode='years'
                      inputProps={{
                        id: 'switchToDate',
                        className: 'form-control',
                        name: 'switchToDate',
                        placeholder: 'Zgjedh datën'
                      }}
                    />
                  </div>
                  {toDateValue && (
                    <Button color='link' size='sm' onClick={clearToDate} style={{ padding: '0 5px' }}>
                      ✕
                    </Button>
                  )}
                </div>
              </FormGroup>
            </div>

            {dateError && (
              <div style={{ color: '#dc3545', fontSize: '0.875rem', marginBottom: '15px' }}>
                {dateError}
              </div>
            )}

            <FormGroup style={{ marginBottom: 0 }}>
              <Label className='col-form-label'>Departamenti:</Label>
              <Input
                type='select'
                onChange={(e) => setSelectedDepartment(e.target.value)}
                value={selectedDepartment}
                name='department'
                className='custom-select'
              >
                <option key='all' value='all'>
                  Të gjitha departamentet
                </option>
                {departments &&
                  departments.map((dep) => (
                    <option key={dep._id} value={dep._id}>
                      {dep.name}
                    </option>
                  ))}
              </Input>
            </FormGroup>

            <FormGroup style={{ marginTop: '15px', marginBottom: 0 }}>
              <Label className='col-form-label'>Lloji:</Label>
              <Input
                type='select'
                onChange={(e) => setSelectedType(e.target.value)}
                value={selectedType}
                name='changeType'
                className='custom-select'
              >
                <option value='all'>Të gjitha</option>
                <option value='transfer'>Vetëm ndryshimet e pozitës</option>
                <option value='reinstatement'>Vetëm rikthimet</option>
              </Input>
            </FormGroup>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '20px' }}>
            <em>
              Raporton vetëm ndryshimet e pozitës, ndarë nga transferet e departamenteve. Të gjitha
              filtrat janë opsionale.
            </em>
          </p>

          <div className='d-flex justify-content-end' style={{ gap: '10px' }}>
            <Button
              color='success'
              outline
              onClick={handleExport}
              disabled={isExporting}
              style={{ minWidth: '100px' }}
            >
              {isExporting ? (
                <>
                  <CircularProgress size={16} style={{ marginRight: '8px', color: 'inherit' }} />
                  Duke eksportuar...
                </>
              ) : (
                'Eksporto'
              )}
            </Button>
            <Button onClick={handleClose} color='danger' outline disabled={isExporting}>
              Anulo
            </Button>
          </div>
        </Box>
      </Modal>
    </>
  )
}

export default ExportSwitchesModal
